<template>
  <div v-if="account && Object.keys(account).length">
    <v-form
      v-model="valid"
      enctype="multipart/form-data"
      @submit.prevent="submit"
    >
      <v-tabs v-model="tab" grow bg-color="primary" slider-color="secondary">
        <v-tab value="basis">
          <v-icon start>mdi-account</v-icon>
          Basis
        </v-tab>
        <v-tab value="type">
          <v-icon start>mdi-account-group</v-icon>
          Type
        </v-tab>
        <v-tab value="overig">
          <v-icon start>mdi-text-box</v-icon>
          Overig
        </v-tab>
      </v-tabs>

      <v-window v-model="tab" class="mt-4">
        <!-- Tab 1: Basic Info -->
        <v-window-item value="basis">
          <v-card variant="flat">
            <v-card-text>
              <div class="d-flex align-center mb-6">
                <v-avatar
                  size="64"
                  class="mr-4"
                  :color="
                    account.gender === 'm' ? 'blue-lighten-4' : 'pink-lighten-4'
                  "
                >
                  <v-icon
                    size="36"
                    :color="account.gender === 'm' ? 'blue' : 'pink'"
                  >
                    {{
                      account.gender === "m" ? "mdi-face-man" : "mdi-face-woman"
                    }}
                  </v-icon>
                </v-avatar>
                <div>
                  <div class="text-h6">Persoonlijke Gegevens</div>
                  <div class="text-subtitle-2 text-grey">
                    Basisinformatie van de doelgroep
                  </div>
                </div>
              </div>

              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="account.first_name"
                    label="Voornaam"
                    placeholder="Voornaam"
                    variant="outlined"
                    density="comfortable"
                    prepend-inner-icon="mdi-account"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="account.last_name"
                    label="Achternaam"
                    placeholder="Achternaam"
                    variant="outlined"
                    density="comfortable"
                    prepend-inner-icon="mdi-account"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-card class="mb-4 pa-4 bg-grey-lighten-5">
                <v-radio-group
                  v-model="account.gender"
                  inline
                  label="Geslacht"
                  density="comfortable"
                >
                  <v-radio label="Man" value="m">
                    <template #label>
                      <div class="d-flex align-center">
                        <v-icon color="blue" class="mr-1"
                          >mdi-gender-male</v-icon
                        >
                        <span>Man</span>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio label="Vrouw" value="v">
                    <template #label>
                      <div class="d-flex align-center">
                        <v-icon color="pink" class="mr-1"
                          >mdi-gender-female</v-icon
                        >
                        <span>Vrouw</span>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-card>

              <v-text-field
                v-model="birthDate"
                label="Geboortedatum"
                placeholder="DD-MM-YYYY"
                hint="Formaat: DD-MM-YYYY"
                persistent-hint
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar"
                @blur="formatBirthDate"
              ></v-text-field>

              <v-text-field
                v-model="account.legal_number"
                label="BSN"
                placeholder="BSN"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-identifier"
              ></v-text-field>

              <v-autocomplete
                v-model="account.country"
                :items="countries"
                label="Nationaliteit"
                item-title="name"
                item-value="short_name"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-earth"
              ></v-autocomplete>

              <v-text-field
                v-model="account.regional_binding"
                label="Regiobinding"
                placeholder="Regiobinding"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-map-marker"
                disabled
                hint="Dit veld kan niet worden gewijzigd"
                persistent-hint
              ></v-text-field>

              <!-- Locations (Google Places) -->
              <v-card class="pa-4 mb-4 bg-grey-lighten-5">
                <div class="text-subtitle-1 mb-2">Locaties</div>
                <Geocode
                  v-model="selectedLocations"
                  v-model:primary-place-id="primaryPlaceId"
                  :multiple="true"
                  :debug="false"
                  label="Zoek locaties (adres, regio of land)"
                  placeholder="Typ om te zoeken"
                />
              </v-card>

              <v-switch
                v-model="account.vip"
                label="VIP account"
                color="amber-darken-3"
                inset
              >
                <template #label>
                  <div class="d-flex align-center">
                    <v-icon color="amber-darken-3" class="mr-2"
                      >mdi-star</v-icon
                    >
                    <span>VIP account</span>
                  </div>
                </template>
              </v-switch>
            </v-card-text>
          </v-card>
        </v-window-item>

        <!-- Tab 2: Account Type -->
        <v-window-item value="type">
          <v-card variant="flat">
            <v-card-text>
              <div class="d-flex align-center mb-6">
                <v-avatar color="purple-lighten-4" size="64" class="mr-4">
                  <v-icon size="36" color="purple">mdi-account-group</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h6">Categorisatie</div>
                  <div class="text-subtitle-2 text-grey">
                    Specificeer het type account en de doelgroep
                  </div>
                </div>
              </div>

              <v-card class="pa-4 mb-4 bg-grey-lighten-5">
                <div class="text-subtitle-1 mb-2">Classificatie</div>
                <v-select
                  v-model="account.account_lead_id"
                  :items="accountLeads"
                  label="Aanleiding"
                  item-title="name"
                  item-value="id"
                  variant="outlined"
                  density="comfortable"
                  clearable
                  prepend-inner-icon="mdi-file-sign"
                  class="mb-3"
                >
                  <template #selection="{ item }">
                    <v-icon class="mr-2" small>mdi-arrow-right-bold</v-icon>
                    {{ item.raw.name }}
                  </template>
                </v-select>

                <v-select
                  v-model="account.account_category_id"
                  :items="accountCategories"
                  label="Doelgroep"
                  item-title="name"
                  item-value="id"
                  variant="outlined"
                  density="comfortable"
                  clearable
                  prepend-inner-icon="mdi-shape"
                >
                  <template #selection="{ item }">
                    <v-icon class="mr-2" small>mdi-account-group</v-icon>
                    {{ item.raw.name }}
                  </template>
                </v-select>
              </v-card>

              <v-card class="pa-4 mb-4 bg-grey-lighten-5">
                <div class="text-subtitle-1 mb-2">OGGZ Status</div>
                <v-select
                  v-model="account.oggz"
                  :items="['Ja', 'Nee']"
                  label="OGGZ"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-clipboard-text"
                ></v-select>
              </v-card>

              <v-card class="pa-4 bg-grey-lighten-5">
                <div class="text-subtitle-1 mb-2">Complexiteit</div>
                <v-switch
                  v-model="account.psychiatrisch"
                  label="Psychiatrisch"
                  color="red"
                  inset
                  class="mb-1"
                >
                  <template #label>
                    <div class="d-flex align-center">
                      <v-icon color="red" class="mr-2" small>mdi-brain</v-icon>
                      <span>Psychiatrisch</span>
                    </div>
                  </template>
                </v-switch>

                <v-switch
                  v-model="account.mz"
                  label="Complex"
                  color="orange"
                  inset
                  class="mb-1"
                >
                  <template #label>
                    <div class="d-flex align-center">
                      <v-icon color="orange" class="mr-2" small
                        >mdi-layers-triple</v-icon
                      >
                      <span>Complex</span>
                    </div>
                  </template>
                </v-switch>

                <v-switch
                  v-model="account.complex_long"
                  label="Complex Langdurig"
                  color="deep-orange"
                  inset
                >
                  <template #label>
                    <div class="d-flex align-center">
                      <v-icon color="deep-orange" class="mr-2" small
                        >mdi-calendar-clock</v-icon
                      >
                      <span>Complex Langdurig</span>
                    </div>
                  </template>
                </v-switch>
              </v-card>
            </v-card-text>
          </v-card>
        </v-window-item>

        <!-- Tab 3: Additional Info -->
        <v-window-item value="overig">
          <v-card variant="flat">
            <v-card-text>
              <div class="d-flex align-center mb-6">
                <v-avatar color="teal-lighten-4" size="64" class="mr-4">
                  <v-icon size="36" color="teal">mdi-text-box</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h6">Aanvullende Informatie</div>
                  <div class="text-subtitle-2 text-grey">
                    Notities en observaties over het account
                  </div>
                </div>
              </div>

              <v-textarea
                v-model="account.description"
                label="Omschrijving"
                auto-grow
                variant="outlined"
                prepend-inner-icon="mdi-text-box-outline"
                clearable
                rows="3"
                class="mb-4"
              >
                <template #label>
                  <div class="d-flex align-center">
                    <span>Omschrijving</span>
                    <v-tooltip location="top">
                      <template #activator="{ props }">
                        <v-icon class="ml-2" size="small" v-bind="props"
                          >mdi-information-outline</v-icon
                        >
                      </template>
                      <span>Algemene beschrijving van het account</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-textarea>

              <v-textarea
                v-model="account.problem"
                label="Probleem"
                auto-grow
                variant="outlined"
                prepend-inner-icon="mdi-alert-circle-outline"
                clearable
                rows="3"
                class="mb-4"
              >
                <template #label>
                  <div class="d-flex align-center">
                    <span>Probleem</span>
                    <v-tooltip location="top">
                      <template #activator="{ props }">
                        <v-icon class="ml-2" size="small" v-bind="props"
                          >mdi-information-outline</v-icon
                        >
                      </template>
                      <span>Beschrijving van de actuele problematiek</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-textarea>

              <v-textarea
                v-model="account.actions"
                label="Acties"
                auto-grow
                variant="outlined"
                prepend-inner-icon="mdi-checkbox-marked-circle-outline"
                clearable
                rows="3"
              >
                <template #label>
                  <div class="d-flex align-center">
                    <span>Acties</span>
                    <v-tooltip location="top">
                      <template #activator="{ props }">
                        <v-icon class="ml-2" size="small" v-bind="props"
                          >mdi-information-outline</v-icon
                        >
                      </template>
                      <span>Geplande of lopende acties voor dit account</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-textarea>
            </v-card-text>
          </v-card>
        </v-window-item>
      </v-window>

      <v-divider class="my-4"></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          variant="text"
          color="grey-darken-1"
          prepend-icon="mdi-close"
          @click="cancel"
        >
          Annuleren
        </v-btn>
        <v-btn
          variant="elevated"
          color="primary"
          prepend-icon="mdi-content-save"
          :loading="loading"
          @click="submit"
        >
          Opslaan
        </v-btn>
      </v-card-actions>
    </v-form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps, defineEmits } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAccountsStoreRefactor } from "@/stores/accountsStoreRefactor";
import { useAccountCategoriesStoreRefactor } from "@/stores/accountCategoriesStoreRefactor";
import { useAccountLeadsStoreRefactor } from "@/stores/accountLeadsStoreRefactor";
import { useAlertStore } from "@/stores/alertStore";
import countries from "../../assets/countries.json";
import moment from "moment";
import Geocode from "@/components/geocode/Geocode.vue";

// Define props and emits
const props = defineProps({
  accountProp: {
    type: Object,
    default: () => ({}),
  },
  popup: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["saved"]);

// Set up route and router
const route = useRoute();
const router = useRouter();

// Set up stores
const accountsStore = useAccountsStoreRefactor();
const accountCategoriesStore = useAccountCategoriesStoreRefactor();
const accountLeadsStore = useAccountLeadsStoreRefactor();
const alertStore = useAlertStore();

// Component state
const valid = ref(false);
const loading = ref(false);
const tab = ref("basis");
const birthDate = ref("");
const account = ref({
  first_name: "",
  last_name: "",
  gender: "m",
  date_of_birth: "",
  legal_number: "",
  country: "",
  regional_binding: "",
  vip: false,
  account_lead_id: null,
  account_category_id: null,
  oggz: "Nee",
  psychiatrisch: false,
  mz: false,
  complex_long: false,
  description: "",
  problem: "",
  actions: "",
});

// Locations state
const selectedLocations = ref([]); // array of { place_id, formatted_address, ... }
const primaryPlaceId = ref(null);

// Computed properties
const accountCategories = computed(() => {
  return accountCategoriesStore.categories || [];
});

const accountLeads = computed(() => {
  return accountLeadsStore.leads || [];
});

// Methods
const formatBirthDate = () => {
  if (!birthDate.value) {
    account.value.date_of_birth = "";
    return;
  }

  try {
    // Try to parse the date
    const dateObj = moment(birthDate.value, "DD-MM-YYYY");

    if (dateObj.isValid()) {
      // Set the standardized date format in the account object
      account.value.date_of_birth = dateObj.format("YYYY-MM-DD");
      // Format the display value
      birthDate.value = dateObj.format("DD-MM-YYYY");
    } else {
      // If invalid, clear the date_of_birth field
      account.value.date_of_birth = "";
      alertStore.error("Ongeldige datum. Gebruik formaat DD-MM-YYYY.");
    }
  } catch (error) {
    console.error("Error formatting birth date:", error);
    account.value.date_of_birth = "";
  }
};

const loadAccount = async (id) => {
  loading.value = true;
  try {
    const data = await accountsStore.fetchById(id);
    Object.assign(account.value, data);

    // Initialize locations from linked_locations when editing
    if (Array.isArray(data.linked_locations)) {
      // Keep order stable: put primary first so Geocode fallback picks it if parent not bound yet
      const sorted = [...data.linked_locations].sort(
        (a, b) => (b.primary === true) - (a.primary === true),
      );
      selectedLocations.value = sorted
        .map((ll) => ({
          place_id: ll.location?.place_id,
          formatted_address: ll.location?.formatted_address,
          name: ll.location?.name,
          lat: ll.location?.latitude,
          lng: ll.location?.longitude,
        }))
        .filter((x) => x.place_id);
      const primary = sorted.find((ll) => ll.primary);
      primaryPlaceId.value = primary?.location?.place_id || null;
    }

    // Set the birth date display value
    if (account.value.date_of_birth) {
      birthDate.value = moment(account.value.date_of_birth).format(
        "DD-MM-YYYY",
      );
    }

    alertStore.success("Account gegevens geladen");
  } catch (error) {
    console.error("Error loading account:", error);
    alertStore.error(
      "Er is een probleem opgetreden bij het laden van het account",
    );
  } finally {
    loading.value = false;
  }
};

const submit = async () => {
  console.log("AccountForm: Submit called with current account data:", {
    id: account.value.id,
    hasDescription: !!account.value.description,
    hasProblem: !!account.value.problem,
    hasActions: !!account.value.actions,
    descriptionLength: account.value.description?.length || 0,
    problemLength: account.value.problem?.length || 0,
    actionsLength: account.value.actions?.length || 0,
  });
  loading.value = true;

  try {
    let result;
    const accountData = { ...account.value };

    // Ensure proper date format
    if (birthDate.value) {
      formatBirthDate();
    }

    // Map locations payload for backend sync
    accountData.place_ids = (selectedLocations.value || []).map(
      (l) => l.place_id,
    );
    accountData.primary_place_id = primaryPlaceId.value || null;

    if (accountData.id) {
      // Update existing account
      result = await accountsStore.update(accountData);
      alertStore.success("Account succesvol bijgewerkt");
    } else {
      // Create new account
      result = await accountsStore.create(accountData);
      alertStore.success("Account succesvol aangemaakt");
    }

    // Emit saved event for form dialog
    emit("saved", result);

    // Navigate to the account detail page if not in popup mode
    if (!props.popup) {
      router.push(`/accounts/${result.id}`);
    }
  } catch (error) {
    // The store now handles error display with specific API messages
    // so we don't need to show a generic error here
    console.error("Error saving account:", error);
  } finally {
    loading.value = false;
  }
};

const cancel = () => {
  // Navigate back to accounts list or account detail
  if (!props.popup) {
    if (account.value.id) {
      router.push(`/accounts/${account.value.id}`);
    } else {
      router.push("/accounts");
    }
  }
};

// Initialize the form
onMounted(async () => {
  try {
    console.log("AccountForm mounted - accountProp:", props.accountProp);

    // If account prop is provided (from FormHolder), use it first
    if (props.accountProp && Object.keys(props.accountProp).length > 0) {
      console.log("Initializing account from props:", props.accountProp);

      // Store backup of critical fields to prevent data loss during async operations
      const backupData = {
        description: props.accountProp.description || "",
        problem: props.accountProp.problem || "",
        actions: props.accountProp.actions || "",
      };

      Object.assign(account.value, props.accountProp);

      // Ensure critical fields are preserved
      account.value.description = backupData.description;
      account.value.problem = backupData.problem;
      account.value.actions = backupData.actions;

      console.log("AccountForm: Preserved critical fields:", {
        description: account.value.description?.length || 0,
        problem: account.value.problem?.length || 0,
        actions: account.value.actions?.length || 0,
      });

      // Set the birth date display value if available
      if (account.value.date_of_birth) {
        birthDate.value = moment(account.value.date_of_birth).format(
          "DD-MM-YYYY",
        );
      }

      // Initialize locations from props if present
      if (Array.isArray(props.accountProp.linked_locations)) {
        selectedLocations.value = props.accountProp.linked_locations
          .map((ll) => ({
            place_id: ll.location?.place_id,
            formatted_address: ll.location?.formatted_address,
            name: ll.location?.name,
            lat: ll.location?.latitude,
            lng: ll.location?.longitude,
          }))
          .filter((x) => x.place_id);
        const primary = props.accountProp.linked_locations.find(
          (ll) => ll.primary,
        );
        primaryPlaceId.value = primary?.location?.place_id || null;
      }

      // Data integrity verification function
      const verifyDataIntegrity = () => {
        let restored = false;
        if (!account.value.description && backupData.description) {
          console.warn("AccountForm: Restoring lost description field");
          account.value.description = backupData.description;
          restored = true;
        }
        if (!account.value.problem && backupData.problem) {
          console.warn("AccountForm: Restoring lost problem field");
          account.value.problem = backupData.problem;
          restored = true;
        }
        if (!account.value.actions && backupData.actions) {
          console.warn("AccountForm: Restoring lost actions field");
          account.value.actions = backupData.actions;
          restored = true;
        }
        if (restored) {
          console.log(
            "AccountForm: Data integrity verification completed, fields restored",
          );
        }
      };

      // Schedule integrity check after async operations
      setTimeout(verifyDataIntegrity, 200);
      setTimeout(verifyDataIntegrity, 1000); // Second check for safety
    }

    // Load account categories and leads - wrapped in try/catch to continue even if these fail
    try {
      console.log("Loading account categories and leads");

      // Only load categories if the store doesn't already have them
      if (accountCategoriesStore.categories.length === 0) {
        console.log("Categories not in store, fetching from API");
        await accountCategoriesStore.getAll();
      } else {
        console.log(
          "Using cached categories, already have",
          accountCategoriesStore.categories.length,
          "categories",
        );
      }

      console.log(
        "Categories loaded:",
        accountCategoriesStore.categories.length,
      );
    } catch (err) {
      console.error("Error loading categories:", err);
      alertStore.error("Fout bij het laden van account categorieën");
    }

    try {
      // Only load leads if the store doesn't already have them
      if (accountLeadsStore.leads.length === 0) {
        console.log("Leads not in store, fetching from API");
        await accountLeadsStore.getAll();
      } else {
        console.log(
          "Using cached leads, already have",
          accountLeadsStore.leads.length,
          "leads",
        );
      }

      console.log("Leads loaded:", accountLeadsStore.leads.length);
    } catch (err) {
      console.error("Error loading leads:", err);
      alertStore.error("Fout bij het laden van account leads");
    }

    // If editing an existing account via route (not form), load its data
    if (route.params.accountId && !account.value.id) {
      console.log("Loading account from route param:", route.params.accountId);
      await loadAccount(route.params.accountId);
    }

    console.log("AccountForm initialization complete");
  } catch (error) {
    console.error("Error initializing account form:", error);
    alertStore.error(
      "Er is een probleem opgetreden bij het initialiseren van het formulier",
    );
  }
});
</script>

<style scoped>
/* Add any custom styling here */
</style>
